import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Search from '../components/Search/Search';
import SearchResultsTable from '../components/Search/SearchResultsTable';
import { useApi } from '../hooks/useApi';
import { isProcessing } from '../common/processingStates';
import {
  buildListDocumentsQuery,
  parseFileManagerSearchParams,
} from '../utils/fileManagerQuery';
import './FileManager.css';

const POLLING_CONFIG = {
  intervalMs: 3000,
  maxDurationMs: 180000,
};

const defaultSmartSearchFilters = () => ({
  orderBy: 'updated_at',
  orderDirection: 'desc',
  limit: 100,
  end_at_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
});

const FileManager = () => {
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const initialFilters = useMemo(
    () => parseFileManagerSearchParams(searchParams),
    [searchParamsKey, searchParams]
  );
  const defaultQuery = useMemo(
    () => buildListDocumentsQuery(
      Object.keys(initialFilters).length ? initialFilters : defaultSmartSearchFilters()
    ),
    [initialFilters]
  );

  const [results, setResults] = useState([]);
  const { callApi, loading, error } = useApi();
  const lastQueryRef = useRef(defaultQuery);
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const [isPolling, setIsPolling] = useState(false);
  const [expectProcessingAfterReprocess, setExpectProcessingAfterReprocess] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [appliedFiltersKey, setAppliedFiltersKey] = useState(0);

  const runQuery = useCallback(async (query) => {
    const data = await callApi(query);
    const items = data.listDocuments.items;
    setResults(items);
    return items;
  }, [callApi]);

  const handleSearch = useCallback(async ({ query }) => {
    try {
      lastQueryRef.current = query;
      await runQuery(query);
    } catch (err) {
      console.error('Error en búsqueda:', err);
      setResults([]);
    }
  }, [runQuery]);

  const refreshResults = useCallback(async () => {
    if (!lastQueryRef.current) return;
    try {
      await runQuery(lastQueryRef.current);
    } catch (err) {
      console.error('Error refrescando resultados:', err);
    }
  }, [runQuery]);

  const handleStopPolling = useCallback(() => {
    setExpectProcessingAfterReprocess(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    setIsPolling(true);
    pollingIntervalRef.current = setInterval(() => {
      refreshResults();
    }, POLLING_CONFIG.intervalMs);

    pollingTimeoutRef.current = setTimeout(() => {
      handleStopPolling();
    }, POLLING_CONFIG.maxDurationMs);
  }, [refreshResults, handleStopPolling]);

  const handleReprocessStarted = useCallback(async ({
    status,
    filename,
  }) => {
    if (status === 'QUEUED') {
      setExpectProcessingAfterReprocess(true);
    }

    const filters = {
      search: filename || undefined,
      orderBy: 'updated_at',
      orderDirection: 'desc',
      limit: 20,
    };
    setAppliedFilters(filters);
    setAppliedFiltersKey((key) => key + 1);

    const query = buildListDocumentsQuery(filters);
    lastQueryRef.current = query;
    await runQuery(query);

    if (status === 'QUEUED') {
      startPolling();
    }
  }, [runQuery, startPolling]);

  useEffect(() => {
    if (Object.keys(initialFilters).length > 0) {
      return;
    }
    lastQueryRef.current = defaultQuery;
    runQuery(defaultQuery).catch((err) => {
      console.error('Error cargando documentos:', err);
    });
  }, [defaultQuery, initialFilters, runQuery]);

  useEffect(() => {
    const hasProcessing = results.some((row) => isProcessing(row.status));
    if (hasProcessing) {
      setExpectProcessingAfterReprocess(false);
    }
    if (hasProcessing || expectProcessingAfterReprocess) {
      startPolling();
    } else {
      handleStopPolling();
    }
  }, [results, expectProcessingAfterReprocess, startPolling, handleStopPolling]);

  useEffect(() => () => handleStopPolling(), [handleStopPolling]);

  return (
    <div className="upload-files-page">
      <Header />
      <div className="upload-files-content">
        <div className="upload-files-main">
          <Search
            onSearch={handleSearch}
            loading={loading}
            initialFilters={Object.keys(initialFilters).length ? initialFilters : null}
            initialFiltersKey={Object.keys(initialFilters).length ? searchParamsKey : null}
            appliedFilters={appliedFilters}
            appliedFiltersKey={appliedFilters ? appliedFiltersKey : null}
          />

          {loading && <p>Cargando resultados...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}

          {isPolling && (
            <div className="file-manager-polling-banner">
              <span>
                Actualizando cada {POLLING_CONFIG.intervalMs / 1000} s mientras se procesa el reproceso…
              </span>
              <button type="button" onClick={handleStopPolling}>
                Detener
              </button>
            </div>
          )}

          <SearchResultsTable
            data={results}
            title="Search Results"
            onRefresh={refreshResults}
            onReprocessStarted={handleReprocessStarted}
          />
        </div>
      </div>
    </div>
  );
};

export default FileManager;
